// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@werner/App/Components/Common/SectionTitle";
import { getNotifyStyle } from "@werner/App/Components/Notify";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    // XNG-Changes
    title = "title",
    sectionTitleUrl = "sectionTitleUrl",
    sectionTitleText = "sectionTitleText",
    orderStatuses = "orderStatuses",
    showTopBorder = "showTopBorder",
}

interface OrderStatus {
    fields: {
        status: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.title]: string;
        [fields.sectionTitleUrl]: LinkFieldValue;
        [fields.sectionTitleText]: string;
        [fields.orderStatuses]: OrderStatus[];
        [fields.showTopBorder]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);
    return {
        ordersDataView: getOrdersDataView(state, {
            ...recentOrdersParameter,
        }),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Changes
        orderStatusMapping: orderStatusMappingDataView,
        widgetPageUrl: getPageLinkByNodeId(state, ownProps.fields.sectionTitleUrl.value),
    };
};

const mapDispatchToProps = {
    loadOrders,
    loadOrderStatusMappings,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    // XNG-Change
    tableWrapper?: GridItemProps;
    wrapper?: InjectableCss;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    tableWrapper: {
        css: css`
            overflow: auto;
        `,
    },
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },
    noOrdersFoundText: {
        ...getNotifyStyle("warning"),
    },
};

// XNG-Changes: export
export const recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC,erpOrderNumber DESC,webOrderNumber DESC",
    expand: ["orderLines"],
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        // XNG-Changes: Load Order Status Mapping
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (!this.props.orderStatusMapping.value && !this.props.orderStatusMapping.isLoading) {
            this.props.loadOrderStatusMappings();
        }

        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }
    // XNG-Changes: handle status filtering parameters
    componentDidUpdate(prevProps: Props) {
        // Handle status parameters
        if (prevProps.orderStatusMapping.value !== this.props.orderStatusMapping.value) {
            const statusFilters = this.props.fields.orderStatuses.map(item => item.fields.status);
            if (statusFilters.length > 0 && this.props.orderStatusMapping.value) {
                const mappedStatus: string[] = [];
                statusFilters.forEach(status => {
                    this.props.orderStatusMapping.value?.forEach(value => {
                        if (value.displayName === status) {
                            mappedStatus.push(value.erpOrderStatus);
                        }
                    });
                });
                recentOrdersParameter.status = mappedStatus;

                if (recentOrdersParameter.status.length > 0) {
                    this.props.loadOrders(recentOrdersParameter);
                }
            }
        }
    }

    render() {
        const { ordersDataView, settingsCollection, canViewOrders, orderStatusMapping, widgetPageUrl, fields } =
            this.props;
        const link = widgetPageUrl?.url || undefined;

        if (!canViewOrders || !ordersDataView.value || !orderStatusMapping.value || ordersDataView.value.length === 0) {
            return null;
        }
        // XNG-Change: change structure to match design, add SectionTitle
        return (
            <StyledWrapper {...styles.wrapper}>
                <SectionTitle
                    title={this.props.fields.title}
                    viewMoreLink={link}
                    viewMoreText={fields.sectionTitleText}
                    border={this.props.fields.showTopBorder ? "top" : "none"}
                    data-test-selector="cardListRecentOrders"
                />
                {ordersDataView.value.length === 0 && (
                    <GridItem width={12}>
                        <Typography {...styles.noOrdersFoundText}>{translate("No orders found")}</Typography>
                    </GridItem>
                )}

                {ordersDataView.value.length > 0 && (
                    <GridItem {...styles.tableWrapper} width={12}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader>{translate("Order Date")}</DataTableHeader>
                                <DataTableHeader>{translate("Order #")}</DataTableHeader>
                                <DataTableHeader>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader>{translate("Ship-To")}</DataTableHeader>
                                <DataTableHeader>{translate("Status")}</DataTableHeader>
                                <DataTableHeader>{translate("Total")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {ordersDataView.value.map(
                                    order =>
                                        order !== null && (
                                            <DataTableRow
                                                className="product-row"
                                                data-test-selector="recentOrders_dataLine"
                                                key={order.id}
                                            >
                                                <OrderSummaryCard
                                                    order={order}
                                                    orderSettings={settingsCollection.orderSettings}
                                                    extendedStyles={styles.orderSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            // XNG-Changes
            {
                name: fields.title,
                displayName: "Widget Title",
                editorTemplate: "TextField",
                defaultValue: "Recent Orders",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.sectionTitleUrl,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                sortOrder: 3,
                isRequired: true,
            },
            {
                name: fields.sectionTitleText,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "View all",
                fieldType: "Translatable",
                sortOrder: 4,
            },
            {
                name: fields.orderStatuses,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.status;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "status",
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "Werner-OrderHistoryStatusMapping",
                        defaultValue: "",
                        tab: basicTab,
                    },
                ],
                sortOrder: 5,
            },
            {
                name: fields.showTopBorder,
                displayName: "Show Top Border ",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
